import React, { useState, useEffect } from 'react';
import styles from './SearchPersonaModal.module.css';
import { exploreIcon } from '../../assets/icons';
import { api } from '../../api';
import type { PersonaListResponse } from '../../api/types';

interface SearchPersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (persona: PersonaListResponse) => void;
}

function SearchPersonaModal({ isOpen, onClose, onSelect }: SearchPersonaModalProps): JSX.Element | null {
  const [personas, setPersonas] = useState<PersonaListResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPersonas();
    }
  }, [isOpen]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchQuery) {
        searchPersonas(searchQuery);
      } else {
        loadPersonas();
      }
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery]);

  const loadPersonas = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.personas.list();
      setPersonas(data);
    } catch (err) {
      setError('Failed to load personas');
      console.error('Failed to load personas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const searchPersonas = async (query: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.personas.search(query);
      setPersonas(data);
    } catch (err) {
      setError('Failed to search personas');
      console.error('Failed to search personas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (persona: PersonaListResponse): void => {
    onSelect(persona);
    onClose(); // Only close the search modal
    setSearchQuery(''); // Reset search when closing
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Personas</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.searchContainer}>
          <img src={exploreIcon} alt="" className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search personas..."
            className={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.personaList}>
          {error ? (
            <div className={styles.error}>{error}</div>
          ) : isLoading ? (
            <div className={styles.loading}>Loading...</div>
          ) : personas.length === 0 ? (
            <div className={styles.empty}>No personas found</div>
          ) : (
            personas.map(persona => (
              <button
                key={persona.id}
                className={styles.personaItem}
                onClick={() => handleSelect(persona)}
              >
                <img 
                  src={persona.icon} 
                  alt={persona.display_name} 
                  className={styles.personaIcon}
                />
                <span className={styles.personaName}>{persona.display_name}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchPersonaModal; 